/** A rectangle in world pixels. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Where `box` goes so that it covers none of `obstacles` (each already grown
 * by the gap to keep) while staying inside `bounds`: the shortest step past
 * a covering obstacle, tried on its four sides, the nearest that is clear of
 * them all. A step that leaves the bounds or lands on another obstacle is
 * set aside; when every step is, the box stays where it was.
 */
export function clearOf(box: Box, obstacles: readonly Box[], bounds: Box): Box {
  const blocking = obstacles.filter((obstacle) => overlaps(box, obstacle));
  if (blocking.length === 0) return box;
  const fits = (candidate: Box): boolean =>
    candidate.x >= bounds.x &&
    candidate.y >= bounds.y &&
    candidate.x + candidate.w <= bounds.x + bounds.w &&
    candidate.y + candidate.h <= bounds.y + bounds.h &&
    obstacles.every((obstacle) => !overlaps(candidate, obstacle));
  let best: Box | undefined;
  let bestStep = Infinity;
  for (const obstacle of blocking) {
    const candidates: Box[] = [
      { ...box, x: obstacle.x - box.w },
      { ...box, x: obstacle.x + obstacle.w },
      { ...box, y: obstacle.y - box.h },
      { ...box, y: obstacle.y + obstacle.h },
    ];
    for (const candidate of candidates) {
      const step = Math.abs(candidate.x - box.x) + Math.abs(candidate.y - box.y);
      if (step < bestStep && fits(candidate)) {
        best = candidate;
        bestStep = step;
      }
    }
  }
  return best ?? box;
}
